import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Fetches the current user's effective Access Matrix grant once per session.
 * Returns `{ unrestricted, pages }` shaped for getNavForUser({ access }).
 * Auth admins & legacy (no access_role) users come back `unrestricted: true`
 * so the sidebar renders exactly as before.
 */
export function useEffectiveAccess() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!user) { setAccess(null); return undefined; }
    const fetchAccess = (initial) => api.get("/access/me")
      .then(({ data }) => {
        if (!alive) return;
        const next = { unrestricted: !!data.unrestricted, pages: data.pages || [], levels: data.levels || null, capabilities: data.capabilities || [] };
        setAccess((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      })
      .catch(() => { if (alive && initial) setAccess({ unrestricted: true, pages: [] }); }); // fail-open on error
    fetchAccess(true);
    // Re-check on tab focus + every 5 min so admin changes apply without a reload.
    const onFocus = () => { if (document.visibilityState !== "hidden") fetchAccess(false); };
    const iv = setInterval(onFocus, 5 * 60 * 1000);
    window.addEventListener("focus", onFocus);
    return () => { alive = false; clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [user]);

  return access;
}
